// Readiness gate: an objective's required props must be dft or user grade.
// Estimates are visible but gated behind explicit consent (see LatticeWizard).
export function assessReadiness(objective, props) {
  const missing = [];
  const estimateGrade = [];
  for (const key of objective?.required_props || []) {
    const p = props?.[key];
    if (!p || p.value == null) missing.push(key);
    else if (p.source === 'estimate') estimateGrade.push(key);
  }
  return { ready: missing.length === 0 && estimateGrade.length === 0, missing, estimateGrade };
}

// Assumed Poisson ratio used to back out E and G from a bulk-modulus-only
// cached analysis (dft_elastic gives bulk modulus alone). Mid-range for a
// generic solid; overridable by the user or superseded by a real elastic
// tensor job (dft_elastic_tensor / DFT chaining in the wizard).
const ASSUMED_POISSON = 0.27;

function round2(n) { return Math.round(n * 100) / 100; }

// DFT cannot supply strength, elongation or fracture toughness in v1: given a
// known Young's modulus (measured or estimated, from any source), fill in
// theoretical/empirical bounds for the remaining fields at estimate grade.
// Shared by buildLatticeProps below and by LatticeWizard's post-DFT step: once
// the elastic tensor DFT job lands a fresh Young's modulus, any objective
// still missing strength/elongation/toughness gets these same derived
// estimates instead of staying stuck on `missing` with nothing to act on.
// Never overwrites an existing value (dft, user, or estimate). Returns a new
// object; does not mutate the input.
export function deriveEstimateDefaults(props, youngsModulusGpa) {
  const next = { ...props };
  if (youngsModulusGpa == null) return next;
  const youngsMpa = youngsModulusGpa * 1000;
  if (next.compressive_strength_mpa == null) {
    next.compressive_strength_mpa = { value: round2(youngsMpa / 20), source: 'estimate', confidence: 0.4 };
  }
  if (next.elongation == null) {
    next.elongation = { value: 0.01, source: 'estimate', confidence: 0.4 };
  }
  if (next.fracture_toughness_mpa_m05 == null) {
    next.fracture_toughness_mpa_m05 = { value: 2.0, source: 'estimate', confidence: 0.4 };
  }
  return next;
}

// Overlay freshly upgraded values (from the DFT chaining in LatticeWizard)
// onto a base prop set (candidate.props + userEdits), honoring provenance:
// user > dft, but ONLY for a genuine, non-null user value. A userEdits entry
// of { value: null, source: 'user' } -- e.g. the user typed a value into the
// property table then cleared the field -- is not a real override; treating
// it as one let a stale null silently shadow a fresh DFT result forever
// (readiness assessed the wrong prop set, and material_props submitted the
// null straight through). Pure; returns a new object, never mutates either
// input. Both LatticeWizard's live `props` (candidate.props + userEdits,
// overlaid with state.upgradedProps) and its DFT_COMPLETE handler (assessing
// readiness on candidate.props + userEdits, overlaid with the just-upgraded
// values) must use this same precedence, or the readiness a submission was
// gated on can disagree with what actually gets submitted.
export function mergeUpgradedProps(baseProps, upgradedProps) {
  const merged = { ...baseProps };
  for (const [key, value] of Object.entries(upgradedProps || {})) {
    const current = merged[key];
    const isRealUserOverride = current && current.source === 'user' && current.value != null;
    if (!isRealUserOverride) merged[key] = value;
  }
  return merged;
}

// Assemble the initial material_props contract for the lattice wizard from a
// candidate's structural density plus whatever DFT analyses are cached for
// it. Values already present on candidate.props (e.g. a prior user edit) are
// never overwritten, so callers can round-trip user overrides through this
// helper safely. See docs/superpowers/sdd/2026-08-17-lattice-service-macro-v1
// Task 12 brief, Step 5, for the exact formulas.
export function buildLatticeProps(candidate, cachedAnalyses = {}) {
  const props = { ...(candidate?.props || {}) };

  if (props.density_g_cc == null && candidate?.density != null) {
    props.density_g_cc = { value: candidate.density, source: 'dft', confidence: 0.9 };
  }

  const tensor = cachedAnalyses?.dft_elastic_tensor;
  const elastic = cachedAnalyses?.dft_elastic;

  if (props.youngs_modulus_gpa == null) {
    if (tensor?.youngs_modulus_gpa != null) {
      props.youngs_modulus_gpa = { value: tensor.youngs_modulus_gpa, source: 'dft', confidence: 0.8 };
    } else if (elastic?.bulk_modulus_GPa != null) {
      const e = 3 * elastic.bulk_modulus_GPa * (1 - 2 * ASSUMED_POISSON);
      props.youngs_modulus_gpa = { value: round2(e), source: 'estimate', confidence: 0.4 };
    }
  }
  if (props.shear_modulus_gpa == null) {
    if (tensor?.shear_modulus_gpa != null) {
      props.shear_modulus_gpa = { value: tensor.shear_modulus_gpa, source: 'dft', confidence: 0.8 };
    } else if (elastic?.bulk_modulus_GPa != null) {
      const e = 3 * elastic.bulk_modulus_GPa * (1 - 2 * ASSUMED_POISSON);
      const g = e / (2 * (1 + ASSUMED_POISSON));
      props.shear_modulus_gpa = { value: round2(g), source: 'estimate', confidence: 0.4 };
    }
  }
  if (props.poisson == null && tensor?.poisson_ratio != null) {
    props.poisson = { value: tensor.poisson_ratio, source: 'dft', confidence: 0.8 };
  }
  if (props.speed_of_sound_ms == null && tensor?.speed_of_sound_ms != null) {
    props.speed_of_sound_ms = { value: tensor.speed_of_sound_ms, source: 'dft', confidence: 0.8 };
  }

  // DFT cannot supply strength, elongation or fracture toughness in v1: fall
  // back to theoretical/empirical bounds once a Young's modulus is known.
  if (props.youngs_modulus_gpa != null) {
    Object.assign(props, deriveEstimateDefaults(props, props.youngs_modulus_gpa.value));
  }

  return props;
}
